import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
} from '@mui/material';
import { Close, CameraAlt, PhotoCamera } from '@mui/icons-material';
import { colors, typography } from '../../theme/designSystem';

interface CameraCaptureModalProps {
  open: boolean;
  onClose: () => void;
  /** Recebe a foto capturada como data URL (JPEG) para cair no quadro de foto. */
  onCapture: (dataUrl: string) => void;
}

// Captura de foto pela câmera do dispositivo (getUserMedia). A imagem capturada volta como
// data URL e entra no mesmo fluxo do arquivo selecionado (ajuste/recorte/salvar). A câmera
// é SEMPRE liberada ao fechar/desmontar — não deixar o LED/stream ligado.
const CameraCaptureModal: React.FC<CameraCaptureModalProps> = ({ open, onClose, onCapture }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [retry, setRetry] = useState(0);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setReady(false);
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    let cancelled = false;
    setError(null);
    setPermissionDenied(false);
    setReady(false);

    const start = async () => {
      // getUserMedia exige contexto seguro (HTTPS/localhost) e navegador compatível.
      if (!navigator.mediaDevices?.getUserMedia) {
        setError('Este navegador não permite acesso à câmera (ou a página não está em HTTPS).');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => undefined);
        }
        setReady(true);
      } catch (e) {
        const name = (e as DOMException)?.name;
        if (name === 'NotAllowedError' || name === 'SecurityError') {
          setPermissionDenied(true);
          setError('Permissão de câmera negada.');
        } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
          setError('Nenhuma câmera encontrada neste dispositivo.');
        } else if (name === 'NotReadableError') {
          setError('A câmera já está em uso por outro aplicativo.');
        } else {
          setError('Não foi possível acessar a câmera.');
        }
      }
    };

    start();

    return () => {
      cancelled = true;
      stopStream();
    };
  }, [open, stopStream, retry]);

  const handleCapture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    stopStream();
    onCapture(dataUrl);
    onClose();
  };

  const handleClose = () => {
    stopStream();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.3)', overflow: 'hidden' } }}
    >
      <DialogTitle
        sx={{
          backgroundColor: colors.primary,
          color: colors.white,
          padding: '1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="h6"
          component="h3"
          sx={{ fontSize: '1.4rem', fontWeight: typography.fontWeight.semibold, margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          <CameraAlt /> Tirar foto
        </Typography>
        <IconButton onClick={handleClose} sx={{ color: colors.white, padding: '0.25rem', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: '1.5rem !important', paddingTop: '1.5rem !important' }}>
        {error ? (
          <Box>
            <Typography sx={{ fontSize: '1rem', color: colors.error, lineHeight: 1.5 }}>{error}</Typography>
            {permissionDenied && (
              <>
                <Typography sx={{ fontSize: '0.875rem', color: colors.textSecondary, mt: 1.5, lineHeight: 1.6 }}>
                  O navegador não deixa a página abrir a própria tela de permissão. Para liberar:
                  toque no ícone à <b>esquerda do endereço</b> (cadeado ou controles),
                  abra <b>Permissões</b> e autorize a <b>Câmera</b>. Depois volte aqui e tente de novo.
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', color: colors.textSecondary, mt: 1, lineHeight: 1.6 }}>
                  Se preferir, use <b>Selecionar</b> para escolher uma foto já tirada.
                </Typography>
                <Button
                  onClick={() => setRetry((n) => n + 1)}
                  variant="outlined"
                  sx={{ mt: 2, textTransform: 'none', color: colors.primary, borderColor: colors.primary }}
                >
                  Tentar novamente
                </Button>
              </>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: '4 / 3',
              backgroundColor: '#000',
              borderRadius: '8px',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            {!ready && (
              <Typography sx={{ position: 'absolute', color: colors.white, fontSize: '0.9rem' }}>
                Iniciando a câmera...
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: '1.5rem 2rem', borderTop: `1px solid ${colors.backgroundAlt}`, backgroundColor: colors.background, gap: '1rem' }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            padding: '0.75rem 1.5rem',
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            backgroundColor: colors.white,
            color: colors.textSecondary,
            fontSize: '1rem',
            fontWeight: typography.fontWeight.medium,
            textTransform: 'none',
            '&:hover': { backgroundColor: colors.background, borderColor: '#adb5bd' },
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleCapture}
          disabled={!ready || Boolean(error)}
          variant="contained"
          startIcon={<PhotoCamera />}
          sx={{
            padding: '0.75rem 1.5rem',
            borderRadius: '6px',
            backgroundColor: colors.primary,
            color: colors.white,
            fontSize: '1rem',
            fontWeight: typography.fontWeight.medium,
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': { backgroundColor: colors.primaryHover, boxShadow: 'none' },
          }}
        >
          Capturar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CameraCaptureModal;
